/**
 * useRim — Reactive rim set change hook.
 *
 * Watches `selectedRimId` in the store. Whenever it changes, performs
 * a hard switch (hide old → show new, no animation) to swap the visible
 * rim set (e.g. OEM wheels → aftermarket wheels).
 *
 * This hook only handles *changes* after the initial load. The initial rim
 * registration and show is handled by the engine:
 *   - EngineConfig.baseNodes for OEM sets (nodeName-based, registered before
 *     setModelScene so the base node is wired + visible from the start)
 *   - EngineConfig.objects for external sets (URL-based, loaded eagerly)
 *   - useEngine sets store.selectedRimId = defaultId after sceneLoaded
 *
 * Separation of concerns:
 *   useEngine    — initial rim registration + show (via EngineConfig)
 *   useRim       — subsequent hard-switches (driven by store)
 *
 * Loading indicator:
 *   Sets store.rimLoading = true when a swap is initiated.
 *   Listens for `assetVisibilityChanged` on the engine — once the NEW rim
 *   becomes visible (id matches, visible: true), clears rimLoading.
 *   The RimSubMenu reads rimLoading to show a "Loading…" hint.
 *
 * Call site: Viewer.tsx (has access to both engineRef and config)
 *
 * @see packages/brand-audi/src/hooks/useEngine.ts — initial rim setup
 * @see docs/ims-engine-api/13-feature-node-swap.md
 */
import { useEffect, useRef } from 'react';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';

export function useRim(
  engineRef: React.RefObject<Engine | null>,
  config: ResolvedConfig | null,
): void {
  const selectedRimId = useAppStore((s) => s.selectedRimId);
  const setRimLoading = useAppStore((s) => s.setRimLoading);

  // Track the previous rim id so we skip the first synchronisation pulse
  // that fires when useEngine already showed the default rim on sceneLoaded.
  const prevRimIdRef = useRef<string | null | undefined>(undefined);

  // --- Rim swap on selection change ---------------------------------------
  useEffect(() => {
    // Skip the boot-up transitions:
    //   undefined → null  (hook initial run, store not yet populated)
    //   null      → id    (useEngine called setSelectedRim(defaultId) after
    //                      sceneLoaded — the base node is already visible,
    //                      calling showAsset would be a no-op or error)
    // Only genuine user-driven changes (id → different id) should trigger.
    if (prevRimIdRef.current === undefined || prevRimIdRef.current === null) {
      prevRimIdRef.current = selectedRimId;
      return;
    }

    // No-op if rim didn't actually change
    if (selectedRimId === prevRimIdRef.current) return;
    prevRimIdRef.current = selectedRimId;

    if (!selectedRimId || !config?.model.rims) return;
    const engine = engineRef.current;
    if (!engine) return;

    // --- Loading indicator -------------------------------------------------
    // Problem: for already-loaded (cached/base-node) assets, engine.showAsset()
    // calls _notifyVisibility synchronously, so setRimLoading(true) and
    // setRimLoading(false) would land in the same React batch → indicator never
    // paints.
    //
    // Solution:
    //   1. setRimLoading(true) schedules a React update; React commits it in the
    //      next microtask and the browser paints it before the next rAF.
    //   2. requestAnimationFrame defers showAsset until AFTER that paint, so the
    //      two state updates are in separate batches and the indicator is visible.
    //   3. MIN_LOADING_MS ensures the hint is readable even for instant swaps.
    //
    // Note: flushSync is NOT used here. flushSync inside useEffect triggers a
    // React warning ("cannot flush when React is already rendering") in React 18+
    // concurrent mode. The rAF already provides the necessary batch separation.
    const MIN_LOADING_MS = 350;

    setRimLoading(true);

    let engineDone = false;
    let minTimeDone = false;

    const tryHide = () => {
      if (engineDone && minTimeDone) setRimLoading(false);
    };

    const unsub = engine.on('assetVisibilityChanged', ({ id, visible }) => {
      if (visible && id === selectedRimId) {
        unsub();
        engineDone = true;
        tryHide();
      }
    });

    // minimum display timer — fires after MIN_LOADING_MS regardless
    const minTimer = setTimeout(() => {
      minTimeDone = true;
      tryHide();
    }, MIN_LOADING_MS);

    // Defer showAsset until after the browser paints the loading indicator.
    // requestAnimationFrame fires after paint, unlike setTimeout(0) which
    // can be batched into the same frame as the preceding commit.
    const rafId = requestAnimationFrame(() => {
      engine.showAsset(selectedRimId, { animated: false });
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(minTimer);
      unsub();
      setRimLoading(false);
    };
  }, [selectedRimId, engineRef, config, setRimLoading]);
}
